/** Everything the system has told you about your own work.
 *
 *  The first screen in this app that belongs to everybody. Every other one is gated —
 *  the day's list is for whoever writes it, approvals for whoever decides, reports for
 *  approvers — and a responsible person could see their tasks and nothing else. This
 *  is theirs.
 *
 *  It exists because chat is best-effort and nothing else was. A message is dropped
 *  when the day has not been published, when the person has never started the bot, and
 *  when Telegram refuses it; all three are silent, and one of them left a task waiting
 *  for days on an approver who had no way of knowing it existed. Whatever happens to
 *  the message, the row is here.
 *
 *  The shape follows the System screen, which is the app's existing answer to "a list
 *  of things that happened, some of them unread": read rows dimmed rather than hidden,
 *  one button to mark the lot, and nothing ever deleted.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { api } from '@/api/client'
import { Empty, ErrorNote, Spinner, fmtStamp } from '@/components/ui'
import type { Notice } from './Notices.pop'

type Feed = { items: Notice[]; total: number; page: number; page_size: number }

/** The four the firm asked for, plus everything. `day` has no tab of its own: a
 *  colleague signing off is worth recording and not worth a filter. */
const TABS: [string, string][] = [
  ['', 'All'],
  ['work', 'My work'],
  ['approval', 'Waiting on me'],
  ['decision', 'Decisions'],
  ['query', 'Questions'],
]

const TONE: Record<string, string> = {
  approval: 'bg-pend-tint text-pend-ink',
  decision: 'bg-done-tint text-done-ink',
  work: 'bg-primary-tint text-primary',
  query: 'bg-rej-tint text-rej-ink',
  day: 'bg-open-tint text-open-ink',
}

export function Notices() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('')
  const [err, setErr] = useState<unknown>(null)

  const feed = useQuery({
    queryKey: ['notices', tab],
    queryFn: () => api.get<Feed>(
      `/notices?page_size=100${tab ? `&category=${tab}` : ''}`),
  })

  const read = useMutation({
    mutationFn: () => api.post('/notices/read', {}),
    onSuccess: () => {
      setErr(null)
      qc.invalidateQueries({ queryKey: ['notices'] })
      qc.invalidateQueries({ queryKey: ['notices-count'] })
      qc.invalidateQueries({ queryKey: ['notices-recent'] })
    },
    onError: setErr,
  })

  const rows = feed.data?.items ?? []
  const unread = rows.filter((n) => n.state === 'open').length

  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      <div className="filter-bar mb-3">
        {TABS.map(([key, label]) => (
          <button key={key || 'all'} type="button" onClick={() => setTab(key)}
                  aria-pressed={tab === key}
                  className={`px-2.5 h-11 sm:h-8 rounded-md border text-[12.5px] ${
                    tab === key ? 'bg-primary text-primary-on border-primary font-medium'
                                : 'bg-surface border-rule text-ink-2 hover:text-ink'}`}>
            {label}
          </button>
        ))}
        <span className="flex-1" />
        {unread > 0 && (
          <button className="btn" disabled={read.isPending}
                  onClick={() => read.mutate()}>
            Mark all {unread} as read
          </button>
        )}
      </div>

      {feed.isLoading ? <Spinner /> : rows.length === 0 ? (
        <Empty
          title="Nothing yet"
          hint="Anything the system tells you about your tasks, your approvals and your
                day turns up here — whether or not the bot could reach you." />
      ) : (
        <div className="card divide-y divide-rule-2">
          {rows.map((n) => (
            <div key={n.notice_id}
                 className={`flex items-start gap-3 px-4 py-2.5 ${
                   n.state === 'open' ? '' : 'opacity-55'}`}>
              <span className={`mono text-[10px] font-semibold rounded px-1.5 py-px
                                mt-0.5 whitespace-nowrap ${
                        TONE[n.category] ?? TONE.day}`}>
                {n.category}
              </span>
              <div className="min-w-0 flex-1">
                {/* The same sentence the bot sends, rendered once and stored - so the
                    two cannot drift, and re-wording it next month cannot rewrite what
                    somebody was told last week. */}
                <Link to={n.link_path || '/today'}
                      className="text-[13px] leading-snug hover:text-primary">
                  {n.summary}
                </Link>
                {n.detail ? (
                  <p className="text-[12px] text-ink-2 mt-0.5">{n.detail}</p>
                ) : null}
              </div>
              {n.resolved && (
                <span className="text-[11px] text-ink-3 whitespace-nowrap mt-0.5">
                  dealt with
                </span>
              )}
              {/* The firm's zone, not UTC - a notification list whose times are five
                  and a half hours out is worse than one with no times. */}
              <span className="mono text-[11px] text-ink-3 whitespace-nowrap mt-0.5">
                {fmtStamp(n.created_at)}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
